import { useTranslation } from "react-i18next";
import { SearchNormal1, Calendar2, Status } from "iconsax-react";
import { Select, Input, DatePicker, SelectWithCheckbox, type SelectWithCheckboxOption } from "@/shared/ui";
import { FilterResetButton, ViewModeToggle } from "@/shared/components";
import { type Locale } from "@/shared/utils";
import type { EmployeeStatus } from "@/features/hr/employees";

export interface StatusOption {
  label: string;
  value: EmployeeStatus | null;
}

interface Props {
  search: string;
  onSearchChange: (v: string) => void;
  hireDateRange: { start: Date | null; end: Date | null };
  onHireDateRangeChange: (r: { start: Date | null; end: Date | null }) => void;
  statusFilter: EmployeeStatus | null;
  onStatusChange: (v: EmployeeStatus | null) => void;
  statusOptions: StatusOption[];
  socialCategoryFilter: string[];
  onSocialCategoryChange: (v: string[]) => void;
  socialCategoryOptions: SelectWithCheckboxOption<string>[];
  hasActiveFilters: boolean;
  onReset: () => void;
  locale: Locale;
  view: "table" | "cards";
  onViewChange: (v: "table" | "cards") => void;
}

export default function EmployeesFilter({
  search,
  onSearchChange,
  hireDateRange,
  onHireDateRangeChange,
  statusFilter,
  onStatusChange,
  statusOptions,
  socialCategoryFilter,
  onSocialCategoryChange,
  socialCategoryOptions,
  hasActiveFilters,
  onReset,
  locale,
  view,
  onViewChange,
}: Props) {
  const { t } = useTranslation("EmployeesPage");

  function handleDateRangeChange(v: Date | { start: Date | null; end: Date | null } | null) {
    if (!v || v instanceof Date) return;
    onHireDateRangeChange(v);
  }

  return (
    <div className="flex items-center justify-between gap-2 mb-7">
      <div className="flex items-center gap-2">
        <div className="min-w-40">
          <Input
            isSmall
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t("filters.searchPlaceholder")}
            icon={<SearchNormal1 size={14} color="currentColor" />}
          />
        </div>

        <DatePicker
          value={hireDateRange}
          mode="range"
          onChange={handleDateRangeChange}
          locale={locale}
          placeholder={t("filters.hireDate")}
          isSmall
          width="min-w-40"
          icon={<Calendar2 size={16} color="currentColor" />}
          align="left"
        />

        <div className="min-w-40">
          <Select<EmployeeStatus | null>
            options={statusOptions}
            placeholder={t("filters.allStatuses")}
            value={statusFilter}
            onChange={onStatusChange}
            isSmall
            icon={<Status size={16} color="currentColor" />}
            width="w-52"
          />
        </div>

        <div className="min-w-48">
          <SelectWithCheckbox
            options={socialCategoryOptions}
            value={socialCategoryFilter}
            onChange={onSocialCategoryChange}
            placeholder={t("socialCategories.title")}
            isSmall
          />
        </div>

        {hasActiveFilters && (
          <FilterResetButton label={t("filters.reset")} onClick={onReset} />
        )}
      </div>

      <div className="flex items-center gap-3">
        <ViewModeToggle
          value={view}
          onChange={onViewChange}
          tableLabel={t("view.table")}
          cardsLabel={t("view.cards")}
        />
      </div>
    </div>
  );
}

